import { useState } from "react";
import type { ReactNode } from "react";

import { Button, ErrorAlert, Modal } from "@/components/ui";
import type { DnsPreview } from "@/lib/domains";

import { needsAcknowledgement, PreviewBody } from "./PreviewBody";

/**
 * The same impact preview as `ReviewModal` (AC12), for changes that touch many records at once with no
 * single "change" to put in a header: URL forwarding, e-mail forwarding and templates (and, in its own
 * modal, zone import). The caller supplies a plain-language `title`/`description` instead of a `DnsChange`.
 */
export function BatchReviewModal({
  open,
  fqdn,
  title,
  description,
  preview,
  onBack,
  onClose,
  onConfirm,
  confirmLabel = "Confirm and apply",
  applying,
  error,
}: {
  open: boolean;
  fqdn: string;
  title: ReactNode;
  description?: ReactNode;
  preview: DnsPreview;
  /** Goes back to the form this preview came from; omit to show only Cancel. */
  onBack?: () => void;
  onClose: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  applying: boolean;
  error: unknown;
}) {
  const [understood, setUnderstood] = useState(false);
  const ack = needsAcknowledgement(preview);

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={560}
      title={title}
      subtitle={description}
      note="Unrelated records are kept. Changes show as Pending until the DNS provider confirms."
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={onBack ?? onClose}>
            {onBack ? "Back" : "Cancel"}
          </Button>
          <Button
            variant="brand"
            size="lg"
            loading={applying}
            disabledReason={
              ack && !understood ? "Tick the box to confirm you understand the impact" : undefined
            }
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <PreviewBody fqdn={fqdn} preview={preview} understood={understood} onUnderstoodChange={setUnderstood} />
      <ErrorAlert error={error} />
    </Modal>
  );
}
